import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { TrophyIcon, SparkleIcon } from "@phosphor-icons/react";

/**
 * live sale platforms-style win celebration overlay.
 *
 * Fires when a buyer wins a lot (`pin.hammered` over the LiveStream
 * websocket).  Three coordinated layers:
 *   1. Backdrop bloom — radial amber glow with a quick brightness lift.
 *   2. Hero card — trophy + animated count-up of the winning price + lot
 *      title + winner name.  Scales in with a small overshoot bounce.
 *   3. Confetti — ~60 lightweight CSS particles that fall, drift, and
 *      rotate independently.  No external library.
 *
 * Self-wins ("YOU WON!") get a bigger, gold treatment with a personal
 * subhead.  Spectators see a calmer "Sold!" hero with the winner's name.
 *
 * The component auto-closes 4.4s after mount via the parent's onDone().
 */

/**
 * Multi-cannon confetti burst — paper flutter + metallic foil + streamers
 * fired from two angled cannons (bottom-left + bottom-right) plus a
 * central upward fountain.  Uses `canvas-confetti` (Stripe / GitHub use
 * the same lib) on an absolute-positioned canvas above the hero card so
 * the physics look like real falling paper, not CSS particles.
 *
 * Self-wins get a warm gold / amber / champagne palette.  Spectators get
 * a fuller spectrum so the moment still feels festive but stays
 * distinguishable at a glance.
 */
function useConfettiBurst(active, palette) {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!active || !canvasRef.current) return undefined;
    const myConfetti = confetti.create(canvasRef.current, {
      resize: true,
      useWorker: true, // off-main-thread render keeps the UI buttery
    });

    // Side cannons — wide angle, low spread, hot velocity. This is the
    // hallmark Stripe / live sale platforms "burst from below" look.
    const fireSideCannon = (origin, angle) =>
      myConfetti({
        particleCount: 80,
        angle,
        spread: 75,
        startVelocity: 65,
        origin,
        colors: palette,
        ticks: 320,
        gravity: 0.9,
        scalar: 1.1,
        // Stripes + circles + squares look like real paper variety.
        shapes: ["square", "circle", "star"],
        decay: 0.92,
      });

    // Foil "streamer" cannon — long thin rectangles tumble more slowly.
    const fireStreamers = (origin, angle) =>
      myConfetti({
        particleCount: 18,
        angle,
        spread: 55,
        startVelocity: 55,
        origin,
        colors: ["#F5E6A8", "#FFE082", "#FFD54F", "#FFFFFF"],
        ticks: 420,
        gravity: 0.55,
        scalar: 1.6,
        shapes: ["star"],
        decay: 0.94,
      });

    // Central "fountain" — pure upward burst from the trophy area; smaller
    // dustier particles that linger longest and sell the magic.
    const fireFountain = () =>
      myConfetti({
        particleCount: 40,
        angle: 90,
        spread: 100,
        startVelocity: 35,
        origin: { x: 0.5, y: 0.32 },
        colors: palette,
        ticks: 260,
        gravity: 0.6,
        scalar: 0.85,
        shapes: ["circle"],
        decay: 0.9,
      });

    // Stagger the bursts so the eye keeps moving.
    fireSideCannon({ x: 0.05, y: 0.85 }, 65);
    fireSideCannon({ x: 0.95, y: 0.85 }, 115);
    setTimeout(() => fireFountain(), 220);
    setTimeout(() => fireStreamers({ x: 0.15, y: 0.78 }, 75), 380);
    setTimeout(() => fireStreamers({ x: 0.85, y: 0.78 }, 105), 380);
    // Secondary burst at the apex of the count-up animation.
    const t2 = setTimeout(() => {
      fireSideCannon({ x: 0.2, y: 0.7 }, 80);
      fireSideCannon({ x: 0.8, y: 0.7 }, 100);
    }, 950);

    return () => {
      clearTimeout(t2);
      myConfetti.reset();
    };
  }, [active, palette]);

  return canvasRef;
}

/** Smooth count-up from 0 → target over ~1.3 seconds. */
function useCountUp(target, durationMs = 1300, start = 0) {
  const [v, setV] = useState(start);
  useEffect(() => {
    if (!Number.isFinite(target)) { setV(target); return; }
    const t0 = performance.now();
    let frame = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - t0) / durationMs);
      // ease-out cubic — fast burst then settle.
      const eased = 1 - Math.pow(1 - t, 3);
      setV(start + (target - start) * eased);
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target, durationMs, start]);
  return v;
}

export default function WinCelebration({ win, onDone }) {
  // Warm gold palette for personal wins, full spectrum for spectator wins.
  const palette = useMemo(
    () =>
      win?.isSelf
        ? ["#FBBF24", "#F59E0B", "#FCD34D", "#FDE68A", "#FFFFFF", "#FB923C"]
        : ["#1D78AB", "#4FC3F7", "#FBBF24", "#F472B6", "#34D399", "#FFFFFF"],
    [win?.isSelf],
  );
  const canvasRef = useConfettiBurst(!!win, palette);
  const amount = Number(win?.amount || 0);
  const animatedAmount = useCountUp(amount, 1300);

  // Auto-dismiss after 4.4s — long enough to read the hero and watch the
  // confetti drift, short enough not to block the next lot.
  useEffect(() => {
    if (!win) return;
    const t = setTimeout(() => onDone?.(), 4400);
    return () => clearTimeout(t);
  }, [win, onDone]);

  if (!win) return null;
  const isSelf = !!win.isSelf;

  return (
    <AnimatePresence>
      <motion.div
        key={win.key || "celebration"}
        className="pointer-events-none fixed inset-0 z-[1500] overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0, transition: { duration: 0.4 } }}
        data-testid="win-celebration"
      >
        {/* Backdrop bloom */}
        <motion.div
          className="absolute inset-0"
          style={{
            background: isSelf
              ? "radial-gradient(ellipse at 50% 35%, rgba(251,191,36,0.55), rgba(0,0,0,0.55) 65%, rgba(0,0,0,0.8) 100%)"
              : "radial-gradient(ellipse at 50% 35%, rgba(29,120,171,0.45), rgba(0,0,0,0.55) 65%, rgba(0,0,0,0.8) 100%)",
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.35 }}
        />

        {/* Confetti canvas — driven by `canvas-confetti`. Sits between the
            radial bloom and the hero so the trophy reads clearly while
            paper drifts in front of and behind it. */}
        <canvas
          ref={canvasRef}
          className="pointer-events-none absolute inset-0 w-full h-full"
          aria-hidden="true"
        />

        {/* Hero card */}
        <motion.div
          className="absolute inset-x-0 top-[18%] flex flex-col items-center px-6"
          initial={{ scale: 0.6, opacity: 0, y: -20 }}
          animate={{
            scale: 1,
            opacity: 1,
            y: 0,
            transition: { type: "spring", stiffness: 240, damping: 16, delay: 0.05 },
          }}
        >
          {/* Crown / trophy with a soft sparkle ping */}
          <motion.div
            className="relative"
            initial={{ rotate: -8 }}
            animate={{ rotate: [-8, 8, -4, 0] }}
            transition={{ duration: 0.9, ease: "easeOut", delay: 0.15 }}
          >
            <div
              className={`h-20 w-20 rounded-full grid place-items-center ${
                isSelf
                  ? "bg-gradient-to-br from-amber-300 to-orange-500 shadow-[0_0_44px_rgba(251,191,36,0.55)]"
                  : "bg-gradient-to-br from-[#4FC3F7] to-[#1D78AB] shadow-[0_0_44px_rgba(29,120,171,0.55)]"
              }`}
            >
              <TrophyIcon size={42} weight="fill" className="text-zinc-900" />
            </div>
            <SparkleIcon
              size={18}
              weight="fill"
              className="absolute -top-1 -right-2 text-white animate-pulse"
            />
            <SparkleIcon
              size={14}
              weight="fill"
              className="absolute -bottom-1 -left-2 text-white animate-pulse"
              style={{ animationDelay: "0.4s" }}
            />
          </motion.div>

          {/* Headline */}
          <motion.div
            className={`mt-5 font-display font-black tracking-tight leading-none text-center ${
              isSelf ? "text-amber-300" : "text-white"
            }`}
            style={{ fontSize: "clamp(34px, 9vw, 56px)" }}
            initial={{ y: 12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.25, duration: 0.35 }}
          >
            {isSelf ? "YOU WON!" : "SOLD!"}
          </motion.div>

          {/* Subhead — lot title + amount */}
          <motion.div
            className="mt-3 text-white text-center"
            initial={{ y: 10, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4, duration: 0.35 }}
          >
            <div className="text-[15px] font-semibold opacity-90 truncate max-w-[80vw] mx-auto">
              {win.listingTitle || "Live lot"}
            </div>
            <div
              className="font-mono font-black mt-2"
              style={{ fontSize: "clamp(40px, 11vw, 68px)" }}
              data-testid="win-celebration-amount"
            >
              ${Math.round(animatedAmount).toLocaleString()}
            </div>
            {!isSelf && win.winnerName && (
              <div className="mt-1 text-[15px] font-bold opacity-95">
                @{win.winnerName}
              </div>
            )}
            {isSelf && (
              <div className="mt-2 text-amber-200 text-sm font-bold">
                It's yours. Heading to your orders.
              </div>
            )}
          </motion.div>

          {/* Optional lot thumbnail */}
          {win.image && (
            <motion.div
              className="mt-5 rounded-2xl overflow-hidden ring-2 ring-white/25"
              initial={{ scale: 0.7, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.55, type: "spring", stiffness: 200, damping: 18 }}
              style={{ width: 132, height: 132 }}
            >
              <img src={win.image} className="h-full w-full object-cover" alt="" />
            </motion.div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
